/**
 * HTTP customer-note service — gateway `/customers/{id}/notes` endpoints
 * (proxied to taxhive-customer-service, added in TASK-01c).
 *
 * Contract notes (matches the new customer_note routes):
 *  - List:   `GET    /customers/{customer_id}/notes` → `{ items, total, ... }`.
 *  - Create: `POST   /customers/{customer_id}/notes` body `{ body }`.
 *            Author + tenant derived from the auth token.
 *  - Delete: `DELETE /customers/{customer_id}/notes/{note_id}` (204).
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type { NoteService } from './notes.service'
import type { CustomerNote, UpdateNoteInput } from '@/types/note.types'
import { mapNoteFromApi, mapNoteListFromApi } from './mappers'

export function createHttpNoteService(): NoteService {
  return {
    async listForCustomer(customerId: string): Promise<CustomerNote[]> {
      try {
        const res = await apiClient.get(`/customers/${customerId}/notes`)
        return mapNoteListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async create(customerId: string, body: string): Promise<CustomerNote> {
      try {
        const res = await apiClient.post(`/customers/${customerId}/notes`, { body })
        return mapNoteFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async update(customerId: string, id: string, input: UpdateNoteInput): Promise<CustomerNote> {
      try {
        const payload: Record<string, unknown> = {}
        if (input.body !== undefined) payload.body = input.body
        if (input.isPinned !== undefined) payload.is_pinned = input.isPinned
        const res = await apiClient.put(`/customers/${customerId}/notes/${id}`, payload)
        return mapNoteFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async remove(customerId: string, id: string): Promise<void> {
      try {
        await apiClient.delete(`/customers/${customerId}/notes/${id}`)
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
